import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Clock, ShoppingBag, Eye, Gift, Loader2, Users } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ViewOthersWishlistPage = () => {
  const navigate = useNavigate();
  const [shareCode, setShareCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [fetchingShared, setFetchingShared] = useState(true);

  useEffect(() => {
    fetchSharedWishlists();
  }, []);

  const fetchSharedWishlists = async () => {
    try {
      const response = await axios.get(`${API}/wishlists/shared-with-me`, { withCredentials: true });
      setSharedWithMe(response.data.wishlists || []);
      setPendingRequests(response.data.pending_requests || []);
    } catch (error) {
      console.error('Failed to fetch shared wishlists:', error);
    } finally {
      setFetchingShared(false);
    }
  };

  const handleRequestAccess = async () => {
    const code = shareCode.trim();
    if (!code) {
      toast.error('Please enter a share code');
      return;
    }
    // Share code format: emailprefix_6digits (e.g., thefinalshot_362436)
    if (!code.includes('_') || code.length < 8) {
      toast.error('Invalid share code format (e.g., username_123456)');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/wishlists/request-access`, {
        share_code: code
      }, { withCredentials: true });
      
      toast.success(response.data.message || 'Access request sent!');
      setShareCode('');
      fetchSharedWishlists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request access');
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const hoursLeft = Math.ceil((expiry - now) / (1000 * 60 * 60));
    if (hoursLeft <= 0) return { text: 'Expired', color: 'text-red-500' };
    if (hoursLeft <= 24) return { text: `${hoursLeft}h left`, color: 'text-orange-500' };
    const daysLeft = Math.ceil(hoursLeft / 24);
    return { text: `${daysLeft}d left`, color: 'text-green-500' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/wishlist')} className="text-white">
            <ArrowLeft size={22} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">View Wishlists</h1>
            <p className="text-xs text-gray-200">Access friends&apos; wishlists</p>
          </div>
        </div>
      </div>

      {/* Enter Code Section */}
      <div className="p-4">
        <div className="bg-white rounded-xl shadow-lg p-4">
          <h2 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Search size={18} className="text-blue-500" />
            Enter Share Code
          </h2>
          <div className="space-y-3">
            <input
              type="text"
              value={shareCode}
              onChange={(e) => setShareCode(e.target.value)}
              placeholder="e.g., thefinalshot_362436"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-medium"
            />
            <button
              onClick={handleRequestAccess}
              disabled={loading}
              className="w-full bg-contribo-navy text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
              Request Access
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Access requires owner approval. Valid for 96 hours once approved.
          </p>
        </div>
      </div>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div className="px-4 mb-4">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <Clock size={16} className="text-orange-400" />
            Pending Requests
          </h3>
          <div className="space-y-2">
            {pendingRequests.map(req => (
              <div key={req.id} className="bg-orange-500/20 border border-orange-500/30 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{req.wishlist_name}</p>
                    <p className="text-gray-400 text-xs">by {req.owner_name}</p>
                  </div>
                  <span className="text-orange-400 text-xs font-semibold">Waiting...</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accessible Wishlists */}
      <div className="px-4">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Gift size={16} className="text-green-400" />
          Wishlists Shared With Me
        </h3>

        {fetchingShared ? (
          <div className="text-center py-8">
            <Loader2 className="mx-auto text-white animate-spin" size={24} />
          </div>
        ) : sharedWithMe.length === 0 ? (
          <div className="bg-white/10 rounded-xl p-6 text-center">
            <Users className="mx-auto text-gray-500 mb-2" size={32} />
            <p className="text-gray-400 text-sm">No wishlists shared with you yet</p>
            <p className="text-gray-500 text-xs mt-1">Enter a share code above to request access</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sharedWithMe.map(wishlist => {
              const timeLeft = getTimeRemaining(wishlist.access_expires_at);
              return (
                <div
                  key={wishlist.id}
                  onClick={() => navigate(`/wishlist/view/${wishlist.share_code}`)}
                  className="bg-white rounded-xl shadow-lg p-4 cursor-pointer hover:shadow-xl transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-800 truncate">{wishlist.name}</h4>
                      <p className="text-gray-500 text-xs mt-0.5">by {wishlist.owner_name}</p>
                    </div>
                    <span className={`text-xs font-semibold ${timeLeft.color}`}>
                      {timeLeft.text}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <ShoppingBag size={14} /> {wishlist.item_count || 0} items
                    </span>
                    <span className="flex items-center gap-1 text-green-500">
                      <Eye size={14} /> Tap to view
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ViewOthersWishlistPage;
